import { useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { BlogLanguageSwitcher, type BlogLocale } from '@saas-pages/shared/public/blog'
import { Seo } from '@saas-pages/shared/public/seo'
import branding from '../../../branding.config'
import { seoFor } from '@/seo/routes'
import { getAllPosts } from '@/shared/blog/posts'
import { useLocale, useT } from '@/shared/i18n'

const BLOG_LOCALE_STORAGE_KEY = 'dateify.blog.locale'

function formatDate(iso: string, locale: 'ru' | 'en'): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export function BlogIndexPage() {
  const t = useT()
  const { locale, setLocale } = useLocale()
  const posts = getAllPosts(locale)

  useEffect(() => {
    try {
      window.localStorage.setItem(BLOG_LOCALE_STORAGE_KEY, locale)
    } catch {
      // localStorage may be blocked by browser settings.
    }
  }, [locale])

  return (
    <>
      <Seo {...seoFor('blog', locale)} ogImage={branding.ogImage} />

      <div className="mx-auto max-w-3xl">
        <nav aria-label={t.common.dateTools} className="mb-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">
            dateify.ru
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">{t.blog.breadcrumb}</span>
        </nav>

        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-3xl font-bold tracking-normal sm:text-4xl">
            {t.blog.title}
          </h1>
          <BlogLanguageSwitcher
            currentLocale={locale}
            ariaLabel={t.blog.languageAria}
            labels={t.blog.localeName}
            onSelectLocale={(next) => setLocale(next as BlogLocale)}
          />
        </div>
        <p className="mb-10 leading-7 text-muted-foreground">
          {t.blog.description}
        </p>

        {posts.length === 0 ? (
          <p className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
            {t.blog.empty}
          </p>
        ) : (
          <ul className="space-y-6">
            {posts.map((post) => (
              <li key={post.slug}>
                <article className="rounded-lg border bg-card p-6 shadow-sm transition-shadow hover:shadow-md">
                  <Link to="/blog/$slug" params={{ slug: post.slug }} className="group block">
                    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <time dateTime={post.date}>{formatDate(post.date, locale)}</time>
                      {post.tags.length > 0 && (
                        <>
                          <span aria-hidden>·</span>
                          <span>{post.tags.join(', ')}</span>
                        </>
                      )}
                    </div>
                    <h2 className="text-xl font-semibold tracking-normal group-hover:text-primary sm:text-2xl">
                      {post.title}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {post.description}
                    </p>
                  </Link>
                </article>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
